import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import { Link } from "react-router";
import { apiErrorMessage, useGetLazy, usePatch, usePost } from "../../api";
import { EmptyState, FilterBar, FilterField, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { usePermissions } from "../../hooks";
import {
  ISmartShoppingProductMiss,
  ISmartShoppingProductMissesResponse,
  ISmartShoppingProductMissRebuildResult,
  ISmartShoppingProductMissUpdate,
  PermissionAction,
  PermissionResource,
  SmartShoppingProductMissStatus,
} from "../../interfaces";

const PAGE_SIZE = 50;

interface IFilter {
  search: string;
  domain: string;
  market: string;
  status: "" | SmartShoppingProductMissStatus;
  kind: "" | ISmartShoppingProductMiss["kind"];
}

const emptyFilter: IFilter = { search: "", domain: "", market: "", status: "open", kind: "" };

const filterParams = (f: IFilter) => {
  const params = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
  return params;
};

const KIND_LABEL: Record<ISmartShoppingProductMiss["kind"], string> = {
  search: "Search",
  "product-page": "Product page",
  "basket-line": "Basket",
};

const STATUS_VARIANT: Record<SmartShoppingProductMissStatus, string> = {
  open: "warning",
  reported: "info",
  resolved: "success",
};

const STATUS_HINT: Record<SmartShoppingProductMissStatus, string> = {
  open: "Nobody has looked at it yet",
  reported: "Passed to ingestion; waiting for the feed to be added or refreshed",
  resolved: "Entity Mapping should now find it",
};

const withHint = (hint: string, node: React.ReactElement) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{hint}</Tooltip>}>
    <span className="d-inline-block">{node}</span>
  </OverlayTrigger>
);

const MissLabel: React.FC<{ m: ISmartShoppingProductMiss }> = ({ m }) => (
  <div style={{ maxWidth: 420 }}>
    <div className="text-truncate" title={m.query}>
      {m.query || <span className="text-muted">(no query)</span>}
    </div>
    {m.productUrl && (
      <a href={m.productUrl} target="_blank" rel="noreferrer" className="small text-truncate d-block text-muted" title={m.productUrl}>
        {m.productUrl}
      </a>
    )}
    <div className="small text-muted">
      {m.sku && <span className="me-2">SKU {m.sku}</span>}
      {m.brand && <span className="me-2">{m.brand}</span>}
      {m.bestConfidence !== null && <span title="Entity Mapping's best match was below the confidence bar">best {m.bestConfidence.toFixed(2)}</span>}
      {m.unmatchedLines && (
        <span title="Basket matching only stores counts, so it can't say which of these lines missed">
          {m.unmatchedLines.missed} of {m.unmatchedLines.of} lines missed (which ones unknown)
        </span>
      )}
    </div>
  </div>
);

export const SmartShoppingProductMisses: React.FC = () => {
  const [draft, setDraft] = useState(emptyFilter);
  const [applied, setApplied] = useState(emptyFilter);
  const [page, setPage] = useState(0);
  const [notice, setNotice] = useState<{ variant: string; text: string } | null>(null);
  const [editing, setEditing] = useState<ISmartShoppingProductMiss | null>(null);
  const [editStatus, setEditStatus] = useState<SmartShoppingProductMissStatus>("open");
  const [editNote, setEditNote] = useState("");
  const [downloading, setDownloading] = useState(false);
  const { data, loading, error, execute } = useGetLazy<ISmartShoppingProductMissesResponse>();
  const { execute: fetchCsv } = useGetLazy<string>();
  const { execute: patch, loading: saving } = usePatch<ISmartShoppingProductMiss, ISmartShoppingProductMissUpdate>();
  const { execute: post, loading: rebuilding } = usePost<ISmartShoppingProductMissRebuildResult, { dryRun: boolean }>();
  const { can, isAdmin } = usePermissions();
  const canUpdate = isAdmin || can(PermissionAction.UpdateAny, PermissionResource.SmartShopping);

  // `execute` changes identity on auth renders; the ref keeps the effect from refiring.
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const load = useCallback((filter: IFilter, pageIndex: number) => {
    const params = filterParams(filter);
    params.set("limit", String(PAGE_SIZE));
    params.set("skip", String(pageIndex * PAGE_SIZE));
    executeRef.current(`smart-shopping/product-misses?${params}`).catch(() => undefined);
  }, []);

  useEffect(() => load(applied, page), [applied, page, load]);

  const activeCount = Object.values(applied).filter(Boolean).length;
  const total = data?.total ?? 0;

  // Exports everything the filter matches, not just the page on screen.
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const csv = await fetchCsv(`smart-shopping/product-misses.csv?${filterParams(applied)}`, { responseType: "text" });
      if (!csv) return;
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `smart-shopping-product-misses-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice({ variant: "danger", text: "Could not download the CSV." });
    } finally {
      setDownloading(false);
    }
  };

  // Dry run first so the person sees how many rows it would touch before anything is written.
  const rebuild = async () => {
    setNotice(null);
    try {
      const dry = await post("smart-shopping/product-misses/rebuild", { dryRun: true });
      if (!dry) return;
      const ok = window.confirm(
        `Rebuilding from ${dry.sessions.toLocaleString()} sessions (${dry.turns.toLocaleString()} turns) would write ${dry.misses.toLocaleString()} misses. ` +
          "Counts and dates are replaced; statuses and notes are kept. Go ahead?"
      );
      if (!ok) return;
      const done = await post("smart-shopping/product-misses/rebuild", { dryRun: false });
      setNotice({ variant: "success", text: `Rebuilt ${done?.misses.toLocaleString() ?? 0} misses from ${done?.turns.toLocaleString() ?? 0} turns.` });
      load(applied, page);
    } catch (err: any) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) ?? "Rebuild failed." });
    }
  };

  const openEdit = (m: ISmartShoppingProductMiss) => {
    setEditing(m);
    setEditStatus(m.status);
    setEditNote(m.note ?? "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await patch(`smart-shopping/product-misses/${editing._id}`, { status: editStatus, note: editNote.trim() || null });
      setEditing(null);
      load(applied, page);
    } catch (err: any) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) ?? "Could not save the status." });
    }
  };

  return (
    <div>
      <PageHeader
        title="Product misses"
        description="Products shoppers asked Smart Shopping for that Entity Mapping couldn't find, by store and market. The products team uses this to decide which feeds to ingest or refresh."
      />
      <PageToolbar>
        <ToolbarButton label="Sessions" icon="bi-chat-square-text" to="/smart-shopping/sessions" hint="Back to the list of Smart Shopping conversations" />
        <ToolbarButton label="Refresh" icon="bi-arrow-clockwise" onClick={() => load(applied, page)} hint="Reload this list with the current filters" />
        <ToolbarButton
          label={downloading ? "Exporting…" : "Export CSV"}
          icon="bi-download"
          onClick={downloadCsv}
          disabled={downloading}
          hint="Download every miss matching the filters (not just this page) for the products team"
        />
        {canUpdate && (
          <ToolbarButton
            label={rebuilding ? "Rebuilding…" : "Rebuild from sessions"}
            icon="bi-arrow-repeat"
            onClick={rebuild}
            disabled={rebuilding}
            hint="Recount misses from every stored session. Shows how many rows it would write and asks before writing; statuses and notes are kept"
          />
        )}
      </PageToolbar>

      {notice && (
        <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <FilterBar
        onSearch={() => {
          setPage(0);
          setApplied(draft);
        }}
        onClear={() => {
          setDraft({ ...emptyFilter, status: "" });
          setApplied({ ...emptyFilter, status: "" });
          setPage(0);
        }}
        activeCount={activeCount}
        summary={data ? `${total.toLocaleString()} miss${total === 1 ? "" : "es"}` : undefined}
      >
        <FilterField label="Search" hint="Product name, URL, SKU or a conversation id">
          <Form.Control size="sm" value={draft.search} onChange={(e) => setDraft({ ...draft, search: e.target.value })} placeholder="e.g. dash satchel" />
        </FilterField>
        <FilterField label="Store" hint="Domain the shopper searched on; type none for searches across all stores" md={3}>
          <Form.Control size="sm" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} placeholder="e.g. nike.com" />
        </FilterField>
        <FilterField label="Market" hint="Shopper's country, e.g. US or UK" md={2}>
          <Form.Control size="sm" value={draft.market} onChange={(e) => setDraft({ ...draft, market: e.target.value })} placeholder="US" />
        </FilterField>
        <FilterField label="Kind" hint="How the agent looked the product up" md={2}>
          <Form.Select size="sm" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as IFilter["kind"] })}>
            <option value="">Any</option>
            <option value="search">Search</option>
            <option value="product-page">Product page</option>
            <option value="basket-line">Basket</option>
          </Form.Select>
        </FilterField>
        <FilterField label="Status" hint="Where the products team is with it" md={2}>
          <Form.Select size="sm" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as IFilter["status"] })}>
            <option value="">Any</option>
            <option value="open">Open</option>
            <option value="reported">Reported</option>
            <option value="resolved">Resolved</option>
          </Form.Select>
        </FilterField>
      </FilterBar>

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {data && data.items.length === 0 && (
        <EmptyState
          title="No product misses"
          message={activeCount ? "Nothing matches these filters." : "Misses appear here when Entity Mapping can't find a product a shopper asked for."}
          icon="bi-search"
        />
      )}
      {data && data.items.length > 0 && (
        <>
          <Table hover responsive size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Store</th>
                <th>Market</th>
                <th>Product</th>
                <th>Kind</th>
                <th className="text-end" title="Turns that hit this miss; a later search in the same turn that found it is counted under 'recovered'">
                  Turns
                </th>
                <th>Last seen</th>
                <th>Example</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m._id}>
                  <td className="small">{m.domain ?? <span className="text-muted">All stores</span>}</td>
                  <td className="small">{m.market}</td>
                  <td>
                    <MissLabel m={m} />
                  </td>
                  <td className="small text-nowrap">{KIND_LABEL[m.kind]}</td>
                  <td className="text-end text-nowrap">
                    {m.count}
                    {m.recoveredCount > 0 && (
                      <Badge bg="light" text="dark" className="ms-1" title="Turns where a later search in the same turn found a similar product (inferred from the words)">
                        {m.recoveredCount} recovered
                      </Badge>
                    )}
                  </td>
                  <td className="small text-nowrap" title={`First seen ${new Date(m.firstSeenAt).toLocaleString()}`}>
                    {new Date(m.lastSeenAt).toLocaleString()}
                  </td>
                  <td className="small">
                    {m.exampleConversationId ? (
                      <Link to={`/smart-shopping/sessions/${m.exampleConversationId}`} title="Open the latest conversation that hit this miss">
                        Session
                      </Link>
                    ) : (
                      "—"
                    )}
                    {m.conversationIds.length > 1 && <span className="text-muted"> +{m.conversationIds.length - 1}</span>}
                  </td>
                  <td>
                    <Badge bg={STATUS_VARIANT[m.status]} text={m.status === "open" ? "dark" : undefined} title={STATUS_HINT[m.status]}>
                      {m.status}
                    </Badge>
                    {m.status !== "open" && m.countSinceStatus > 0 && (
                      <div className="small text-danger" title="Shoppers still hit this after the status changed">
                        {m.countSinceStatus} since
                      </div>
                    )}
                    {m.note && (
                      <div className="small text-muted text-truncate" style={{ maxWidth: 200 }} title={`${m.note}${m.updatedBy ? ` — ${m.updatedBy}` : ""}`}>
                        {m.note}
                      </div>
                    )}
                  </td>
                  <td className="text-end">
                    {canUpdate &&
                      withHint(
                        "Mark as reported to ingestion or resolved, with a note for the team",
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(m)}>
                          Status
                        </Button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center small text-muted">
            <span>
              Showing {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total.toLocaleString()}
            </span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal show={!!editing} onHide={() => setEditing(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h6">Update miss</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <p className="small text-muted mb-3">
              {editing.query || editing.productUrl} · {editing.domain ?? "all stores"} · {editing.market}
            </p>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="small">Status</Form.Label>
            <Form.Select size="sm" value={editStatus} onChange={(e) => setEditStatus(e.target.value as SmartShoppingProductMissStatus)}>
              <option value="open">Open</option>
              <option value="reported">Reported</option>
              <option value="resolved">Resolved</option>
            </Form.Select>
            <Form.Text className="text-muted">{STATUS_HINT[editStatus]}. Saving resets the "since" count.</Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label className="small">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              value={editNote}
              maxLength={2000}
              onChange={(e) => setEditNote(e.target.value)}
              placeholder="e.g. feed not on Awin; raised with ingestion"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="outline-secondary" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button size="sm" onClick={saveEdit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
